// The family-facing "paperwork complete" email text, kept free of React/MUI
// and the auth client so it can be unit-tested on its own.

/** Death certificates the family ordered, by processing speed (see deathCertificateCounts.ts). */
export type CertificateOrder = { standard: number; expedited: number };

/**
 * The death-certificate timing bullet(s). Accelerated (« rapide ») Québec
 * certificates arrive in about 3–4 weeks instead of 6–8, so when the family
 * paid for any, say so — and when they also ordered regular copies, give both
 * timings (T-132).
 */
export function certificateTimingLines(
  language: 'en' | 'fr',
  order?: CertificateOrder | null,
): string[] {
  const expedited = order?.expedited ?? 0;
  const standard = order?.standard ?? 0;
  const isFrench = language === 'fr';

  if (expedited <= 0) {
    return [
      isFrench
        ? 'Le certificat de décès devrait vous parvenir dans environ 6 à 8 semaines.'
        : 'The death certificate should arrive in about 6–8 weeks.',
    ];
  }

  const lines: string[] = [];
  if (isFrench) {
    lines.push(
      expedited === 1
        ? 'Vous avez commandé le certificat de décès en traitement accéléré : il devrait vous parvenir dans environ 3 à 4 semaines.'
        : `Vous avez commandé ${expedited} copies du certificat de décès en traitement accéléré : elles devraient vous parvenir dans environ 3 à 4 semaines.`,
    );
    if (standard > 0) {
      lines.push(
        standard === 1
          ? "L'autre copie, en traitement normal, devrait vous parvenir dans environ 6 à 8 semaines."
          : 'Les autres copies, en traitement normal, devraient vous parvenir dans environ 6 à 8 semaines.',
      );
    }
  } else {
    lines.push(
      expedited === 1
        ? 'You ordered the death certificate with accelerated processing: it should arrive in about 3–4 weeks.'
        : `You ordered ${expedited} copies of the death certificate with accelerated processing: they should arrive in about 3–4 weeks.`,
    );
    if (standard > 0) {
      lines.push(
        standard === 1
          ? 'The other copy, with regular processing, should arrive in about 6–8 weeks.'
          : 'The other copies, with regular processing, should arrive in about 6–8 weeks.',
      );
    }
  }
  return lines;
}

export function buildPaperworkCompleteMessage(
  language: 'en' | 'fr',
  firstName: string,
  deathCertificateNumber?: string | null,
  benefitsConfirmationNumber?: string | null,
  certificateOrder?: CertificateOrder | null,
): string {
  const isFrench = language === 'fr';
  const certBullets = certificateTimingLines(language, certificateOrder)
    .map((l) => `- ${l}\n`)
    .join('');

  if (isFrench) {
    const greeting = firstName ? `Bonjour ${firstName},` : 'Bonjour,';
    const refsLines: string[] = [];
    if (deathCertificateNumber) {
      refsLines.push(`Numéro de confirmation de la déclaration de décès : ${deathCertificateNumber}`);
    }
    if (benefitsConfirmationNumber) {
      refsLines.push(`Numéro de confirmation des prestations : ${benefitsConfirmationNumber}`);
    }
    const refsBlock = refsLines.length ? `\n\n${refsLines.join('\n')}` : '';
    return `${greeting}\n\nNous avons complété toutes les démarches administratives liées au décès — la déclaration de décès et la demande de prestations ont été soumises.${refsBlock}\n\nÀ titre informatif :\n${certBullets}- Les prestations gouvernementales sont généralement versées dans un délai d'environ 3 mois.\n\nN'hésitez pas à nous écrire si vous avez la moindre question.\n\nChaleureusement,\nL'équipe Cleo`;
  }

  const greeting = firstName ? `Hi ${firstName},` : 'Hi,';
  const refsLines: string[] = [];
  if (deathCertificateNumber) {
    refsLines.push(`Death certificate confirmation number: ${deathCertificateNumber}`);
  }
  if (benefitsConfirmationNumber) {
    refsLines.push(`Benefits confirmation number: ${benefitsConfirmationNumber}`);
  }
  const refsBlock = refsLines.length ? `\n\n${refsLines.join('\n')}` : '';
  return `${greeting}\n\nWe've finished the administrative paperwork — the declaration of death and the benefits application have both been submitted.${refsBlock}\n\nA few timing notes:\n${certBullets}- Government benefits typically take around 3 months to be paid out.\n\nPlease reach out if you have any questions at all.\n\nWarmly,\nThe Cleo Team`;
}
